"use client"

import { Transition } from "@headlessui/react"
import { useId, useState } from "react"
import { Icon } from "@/shared/ui/icon"
import type { AppVersion } from "@lib/store"

type StoreAppVersionsProps = {
  versions: AppVersion[]
}

export function StoreAppVersions({ versions }: StoreAppVersionsProps) {
  const accordionId = useId()
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(
    () => new Set(versions[0] ? [versions[0].version] : []),
  )

  if (versions.length === 0) return null

  return (
    <aside aria-labelledby={`${accordionId}-title`} className="w-full">
      <h2
        id={`${accordionId}-title`}
        className="type-overline text-slate-500 dark:text-white/50"
      >
        История обновлений
      </h2>

      <ol className="relative mt-4 space-y-3 border-l border-slate-200/60 dark:border-white/10">
        {versions.map((version, index) => {
          const isLatest = index === 0
          const isExpanded = expandedVersions.has(version.version)
          const panelId = `${accordionId}-panel-${index}`
          const buttonId = `${accordionId}-button-${index}`

          return (
            <li key={version.version} className="group relative pl-6">
              <span
                aria-hidden="true"
                className="absolute -left-[5px] top-4 flex size-2.5 items-center justify-center"
              >
                <span
                  className={`size-2.5 rounded-full transition-[background-color,box-shadow] duration-300 motion-reduce:transition-none ${
                    isExpanded
                      ? "bg-brand-500 ring-4 ring-brand-500/20 dark:bg-jungle-400 dark:ring-jungle-500/20"
                      : "bg-slate-300 dark:bg-white/20 group-hover:bg-slate-400 dark:group-hover:bg-white/40"
                  }`}
                />
              </span>

              <div
                className={`overflow-hidden rounded-2xl border transition-[border-color,background-color,box-shadow] duration-300 motion-reduce:transition-none ${
                  isExpanded
                    ? "border-slate-200/80 bg-white/70 shadow-lg shadow-slate-200/50 backdrop-blur-sm dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/40"
                    : "border-transparent bg-transparent hover:bg-slate-100/50 dark:hover:bg-white/[0.02]"
                }`}
              >
                <button
                  id={buttonId}
                  type="button"
                  aria-controls={panelId}
                  aria-expanded={isExpanded}
                  onClick={() =>
                    setExpandedVersions((currentVersions) => {
                      const nextVersions = new Set(currentVersions)
                      if (nextVersions.has(version.version)) {
                        nextVersions.delete(version.version)
                      } else {
                        nextVersions.add(version.version)
                      }
                      return nextVersions
                    })
                  }
                  className="flex w-full touch-manipulation items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/50 dark:focus-visible:ring-jungle-400/60"
                >
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
                    <span className="type-numeric text-base font-semibold text-slate-900 dark:text-white">
                      v{version.version}
                    </span>

                    {isLatest ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-brand-500/20 bg-brand-500/10 px-2.5 py-0.5 text-xs font-medium text-brand-700 dark:border-jungle-400/20 dark:bg-jungle-500/10 dark:text-jungle-300">
                        <span
                          aria-hidden="true"
                          className="size-1.5 rounded-full bg-brand-500 dark:bg-jungle-400"
                        />
                        Последняя
                      </span>
                    ) : null}
                  </span>

                  <span className="flex shrink-0 items-center gap-3">
                    <span className="type-numeric whitespace-nowrap text-xs text-slate-500 dark:text-white/50">
                      {version.date}
                    </span>

                    <span
                      className={`flex size-7 items-center justify-center rounded-full border transition-[border-color,background-color,color] duration-200 motion-reduce:transition-none ${
                        isExpanded
                          ? "border-slate-200 bg-slate-100 text-slate-900 dark:border-white/15 dark:bg-white/10 dark:text-white"
                          : "border-transparent text-slate-400 group-hover:text-slate-600 dark:text-white/40 dark:group-hover:text-white"
                      }`}
                    >
                      <Icon
                        name="alt-arrow-down-linear"
                        aria-hidden="true"
                        className={`size-4 transition-transform duration-300 motion-reduce:transition-none ${
                          isExpanded ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    </span>
                  </span>
                </button>

                <Transition
                  show={isExpanded}
                  enter="transition duration-200 ease-out motion-reduce:transition-none"
                  enterFrom="opacity-0 -translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition duration-150 ease-in motion-reduce:transition-none"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 -translate-y-1"
                >
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className="border-t border-slate-100 px-4 pb-4 pt-3 dark:border-white/5"
                  >
                    <div className="rounded-xl bg-slate-50/70 p-3.5 dark:bg-white/[0.02]">
                      <p className="break-words text-sm text-slate-600 dark:text-white/70">
                        {version.changes}
                      </p>
                    </div>
                  </div>
                </Transition>
              </div>
            </li>
          )
        })}
      </ol>
    </aside>
  )
}
